import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGBarChart } from '../../components/primitives/PGChart';
import { GROUP, BRANCHES, REGIONS, MONTHS } from '../../utils/orgData';
import { PG } from '../../utils/theme';
import { useLiveFeed } from '../../components/live/LiveFeedProvider';

const { FiAlertOctagon, FiShield, FiBriefcase, FiLock, FiCheckCircle } = FiIcons;

const OPEN_LEGAL = [
  { ref: 'LGL-2024-017', matter: 'Contractor injury claim — Centurion', stage: 'Discovery', exposure: 'Medium', tone: 'warn', branchId: 'centurion' },
  { ref: 'LGL-2025-002', matter: 'Insurer rebate dispute', stage: 'Mediation', exposure: 'Low', tone: 'info' },
  { ref: 'LGL-2025-009', matter: 'Property lease renegotiation — Maputo', stage: 'Negotiation', exposure: 'Low', tone: 'info', branchId: 'maputo' },
  { ref: 'LGL-2025-014', matter: 'Customer vehicle damage (subrogation) — Pietermaritzburg', stage: 'Filed', exposure: 'High', tone: 'alert', branchId: 'pietermaritzburg' },
];

// Near-miss heatmap: region x month 0-5
function heatValue(regionIdx, monthIdx) {
  const seed = (regionIdx * 13 + monthIdx * 7) % 13;
  return seed % 6;
}
function heatColor(v) {
  if (v >= 4) return PG.red;
  if (v >= 2) return PG.amber;
  return PG.emerald;
}

export default function ComplianceSliver() {
  const feed = useLiveFeed();
  const c = GROUP.kpis.compliance;

  // LTIFR by region using branch.hr.ltifr averaged
  const regionLtifr = REGIONS.map((r) => ({
    name: r.name,
    ltifr: +(r.branches.reduce((s, b) => s + b.hr.ltifr, 0) / r.branches.length).toFixed(2),
  }));

  const toolbox = [...BRANCHES]
    .sort((a, b) => a.compliance.toolboxTalkCompletion - b.compliance.toolboxTalkCompletion)
    .slice(0, 12);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard title="NEAR MISSES (MTD)" value={c.safetyNearMisses} icon={FiAlertOctagon} accent={PG.amber} target="<50" animated="ticker" numericValue={c.safetyNearMisses} tickerDigits={3} />
        <KPICard title="PPE COMPLIANCE" value={c.ppeCompliance} unit="%" icon={FiShield} accent={PG.emerald} target=">95%" sparkValues={feed.throughputPerMin} />
        <KPICard title="LEGAL DISPUTES OPEN" value={c.legalDisputesOpen + OPEN_LEGAL.length} icon={FiBriefcase} accent={PG.amber} />
        <KPICard title="POPIA INCIDENTS" value={c.popiaIncidents} icon={FiLock} accent={c.popiaIncidents > 0 ? PG.red : PG.emerald} target="0" animated="splitflap" flapText={String(c.popiaIncidents).padStart(2, '0')} flapWidth={2} />
        <KPICard title="INSURER AUDIT PASS" value={c.insurerAuditPass} unit="%" icon={FiCheckCircle} accent={PG.emerald} target="100%" />
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel title="LTIFR_BY_REGION" subtitle="lost-time injury freq" className="col-span-5">
          <div className="h-full p-2">
            <PGBarChart
              categories={regionLtifr.map((r) => r.name)}
              series={[
                {
                  name: 'LTIFR',
                  data: regionLtifr.map((r) => ({
                    value: r.ltifr,
                    itemStyle: { color: r.ltifr > 1.5 ? PG.red : r.ltifr > 1 ? PG.amber : PG.emerald },
                  })),
                },
              ]}
            />
          </div>
        </Panel>

        <Panel title="NEAR_MISS_HEATMAP" subtitle="region × month" className="col-span-7">
          <div className="h-full overflow-auto custom-scrollbar p-3">
            <table className="w-full border-separate" style={{ borderSpacing: 3 }}>
              <thead>
                <tr>
                  <th></th>
                  {MONTHS.map((m) => (
                    <th key={m} className="font-mono-tech text-[9px] uppercase tracking-wider text-neutral-500 text-center">{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {REGIONS.map((r, ri) => (
                  <tr key={r.id}>
                    <td className="pr-2 text-xs text-neutral-300 whitespace-nowrap">{r.name}</td>
                    {MONTHS.map((m, mi) => {
                      const v = heatValue(ri, mi);
                      return (
                        <td key={mi} className="text-center">
                          <div
                            className="w-full h-6 flex items-center justify-center font-mono-tech text-[10px] text-black font-bold"
                            style={{ backgroundColor: heatColor(v), opacity: 0.3 + (v / 5) * 0.7 }}
                          >
                            {v}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="TOOLBOX_TALK_COMPLETION_BY_BRANCH" subtitle="worst first" className="col-span-7">
          <div className="h-full p-2">
            <PGBarChart
              horizontal
              categories={toolbox.map((b) => b.name).reverse()}
              series={[
                {
                  name: 'Completion %',
                  data: toolbox
                    .map((b) => ({
                      value: b.compliance.toolboxTalkCompletion,
                      itemStyle: { color: b.compliance.toolboxTalkCompletion < 90 ? PG.red : b.compliance.toolboxTalkCompletion < 95 ? PG.amber : PG.emerald },
                    }))
                    .reverse(),
                },
              ]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel title="OPEN_LEGAL_MATTERS" subtitle={`${OPEN_LEGAL.length} active`} tone="warn" className="col-span-5">
          <div className="h-full overflow-y-auto custom-scrollbar">
            <table className="w-full text-xs">
              <thead className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 bg-black/40 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Ref</th>
                  <th className="text-left px-3 py-2">Matter</th>
                  <th className="text-left px-3 py-2">Stage</th>
                  <th className="text-left px-3 py-2">Exposure</th>
                </tr>
              </thead>
              <tbody>
                {OPEN_LEGAL.map((l) => (
                  <tr
                    key={l.ref}
                    className={`border-t border-white/5 hover:bg-[#D71921]/10 transition-colors ${l.branchId ? 'row-clickable cursor-pointer' : ''}`}
                    onClick={l.branchId ? () => { window.location.hash = `#/sliver/compliance/branch/${l.branchId}`; } : undefined}
                  >
                    <td className="px-3 py-2 font-mono-tech text-[10px] text-neutral-400">{l.ref}</td>
                    <td className="px-3 py-2 text-neutral-300">{l.matter}</td>
                    <td className="px-3 py-2 text-neutral-400 text-xs">{l.stage}</td>
                    <td className="px-3 py-2"><StatusPill tone={l.tone}>{l.exposure}</StatusPill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
